import { useMemo, useCallback, useState, Dispatch, SetStateAction, useEffect } from "react";
import { useDropzone, DropzoneRootProps } from "react-dropzone";
import ReactCrop, { Crop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useIntl } from "react-intl";
import { baseStyle, activeStyle, acceptStyle, rejectStyle } from "./dropzoneStyles";

type Props = {
    crop: Crop | undefined;
    setCrop: Dispatch<SetStateAction<any>>;
};

export const ImageDropzoneCropper = ({ crop, setCrop }: Props) => {
    const [image, setImage] = useState<File>();
    const [preview, setPreview] = useState("");

    const intl = useIntl();

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            setImage(acceptedFiles[0]);
        },
        [setImage]
    );

    const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
        accept: { "image/*": [] },
        onDrop,
        maxFiles: 1,
    });

    const style: DropzoneRootProps = useMemo(
        () => ({
            ...baseStyle,
            ...(isDragActive ? activeStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isDragActive, isDragReject, isDragAccept]
    );

    useEffect(() => {
        if (image) {
            setPreview((window.URL ? URL : webkitURL).createObjectURL(image));
            const reader = new FileReader();
            reader.readAsDataURL(image);
            reader.onload = e => {
                const img = new Image();
                img.src = e.target ? (e.target.result as string) : "";
                if (!!img.src)
                    img.onload = () => {
                        setCrop(centerAspectCrop(img.width, img.height) as Crop);
                    };
            };
        }
    }, [image]);

    const centerAspectCrop = (mediaWidth: number, mediaHeight: number) => {
        return makeAspectCrop(
            {
                unit: "%",
                width: 90,
            },
            390 / 490,
            mediaWidth,
            mediaHeight
        );
    };

    const onImageLoaded = (image: HTMLImageElement) => {
        setCrop(centerAspectCrop(image.width, image.height) as Crop);
    };

    return (
        <div className="image-dropzone-container">
            <section {...getRootProps({ style })}>
                <input {...getInputProps()} />
                {image ? image.name : intl.formatMessage({ id: "dragAndDrop" })}
            </section>
            {image && (
                <ReactCrop crop={crop} onChange={(_, p) => setCrop(p)} aspect={390 / 490}>
                    <img src={preview} alt="Crop" onLoad={e => onImageLoaded(e.currentTarget)} />
                </ReactCrop>
            )}
        </div>
    );
};

export const getCroppedImg = (crop: any) => {
    const parentDiv = document.querySelector<HTMLDivElement>("div.image-dropzone-container");
    if (!parentDiv) return undefined;
    const image = parentDiv.querySelector<HTMLImageElement>("img");
    if (!image) return undefined;
    const canvas = document.createElement("canvas");
    canvas.width = (image.naturalWidth * crop.width) / 100;
    canvas.height = (image.naturalHeight * crop.height) / 100;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(
        image,
        (crop.x / 100) * image.naturalWidth,
        (crop.y / 100) * image.naturalHeight,
        canvas.width,
        canvas.height,
        0,
        0,
        canvas.width,
        canvas.height
    );
    return new Promise(resolve => {
        canvas.toBlob(
            (blob: any) => {
                resolve(blob);
            },
            "image",
            1
        );
    });
};
